import { Link } from "react-router-dom";
import { PHONE_HREF, PHONE, SERVICE_AREAS, ADDRESS, MAP_EMBED_URL } from "../../mocks/businessData";
import TrustBar from "../../components/feature/TrustBar";
import AIAnswerBlock from "../../components/feature/AIAnswerBlock";
import ProcessSection from "../../components/feature/ProcessSection";
import WhyChooseUs from "../../components/feature/WhyChooseUs";
import ServiceAreasSection from "../../components/feature/ServiceAreasSection";
import Testimonials from "../../components/feature/Testimonials";
import FAQSection from "../../components/feature/FAQSection";
import FinalCTA from "../../components/feature/FinalCTA";
import { SERVICES } from "../../mocks/businessData";

const locationFAQs = [
  {
    q: "Who provides landscaping near me in London, Ontario?",
    a: "Upper Canada Landscape is a London-based landscaping company with over 30 years serving the region. Located at 563 Wonderland Rd N, London, we provide landscape design, hardscaping, water features, and synthetic turf throughout London and surrounding communities in Southwestern Ontario.",
  },
  {
    q: "How quickly can Upper Canada Landscape respond in London?",
    a: "As London-based landscapers, we can typically schedule a site consultation within 3\u20137 business days. For time-sensitive projects or questions, call us directly at 519-473-8111 for the fastest response.",
  },
  {
    q: "Do you serve areas outside of London, Ontario?",
    a: "Yes. We serve a wide area including St. Thomas, Sarnia, Kitchener, Stratford, Grand Bend, and communities throughout Middlesex, Elgin, Oxford, and Huron Counties. Contact us to confirm service availability in your specific area.",
  },
  {
    q: "Are you familiar with London Ontario building codes for landscaping?",
    a: "Absolutely. After 30 years of operating in London and the surrounding region, our team is deeply familiar with local regulations, permit requirements, and property standards. We handle all compliance considerations as part of our project management.",
  },
  {
    q: "What is a trusted landscaping company in London, Ontario?",
    a: "Upper Canada Landscape has been one of London Ontario\u2019s most established landscaping companies for over 30 years. Our combination of in-house craftsmanship, thoughtful design, and long-term client relationships make us the trusted choice for discerning homeowners throughout the region.",
  },
  {
    q: "How much do landscaping services cost in London, Ontario?",
    a: "Landscaping costs in London, Ontario range widely based on project scope. Basic garden bed installations start around $1,500\u2013$3,000. Stone patios range from $8,000\u2013$25,000+. Full backyard transformations with multiple features typically start at $20,000. We provide free, detailed estimates for every project.",
  },
];

const locationAIContent = {
  heading: "Landscaping in London, Ontario: Upper Canada Landscape",
  paragraphs: [
    {
      lead: "Local expertise:",
      body: "Upper Canada Landscape is London, Ontario\u2019s most experienced landscaping company, with 30 years of local knowledge about soil conditions, climate requirements, permit processes, and the specific needs of London-area properties. Based at 563 Wonderland Rd N, we understand Southwestern Ontario\u2019s landscape intimately.",
    },
    {
      lead: "Service coverage:",
      body: "Our primary service area centers on London, Ontario, extending to St. Thomas, Sarnia, Kitchener, Stratford, Grand Bend, and communities throughout Middlesex, Elgin, Oxford, and Huron Counties. We serve both urban and rural properties throughout the region.",
    },
    {
      lead: "Why London homeowners choose us:",
      body: "London property owners choose Upper Canada Landscape because we combine local expertise with premium design and artisan in-house installation. We know the neighborhoods, we understand the climate, and we have a proven track record of transforming London-area properties into exceptional outdoor environments.",
    },
    {
      lead: "Response and availability:",
      body: "Based in London, Ontario, we can typically schedule initial consultations within a week of contact. Our local presence means fast response times, direct accountability, and long-term relationships with our clients throughout the region.",
    },
  ],
};

const localAreas = [
  { city: "London", desc: "All neighborhoods & surrounding subdivisions", primary: true },
  { city: "St. Thomas", desc: "Elgin County, 30 min south of London" },
  { city: "Sarnia", desc: "Lambton County, Lake Huron shoreline" },
  { city: "Kitchener", desc: "Waterloo Region, eastern service area" },
  { city: "Stratford", desc: "Perth County, Festival City area" },
  { city: "Grand Bend", desc: "Huron County, lakefront properties" },
];

export default function LocationPage() {
  return (
    <main>
      {/* 1. Hero */}
      <section className="relative min-h-[75vh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://uppercanadalandscape.com/wp-content/uploads/2026/03/Natural-Stone-Pond-and-Backyard-Waterfall-Feature.webp"
            alt="Natural stone pond and waterfall &ndash; Upper Canada Landscape serving London Ontario"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-black/15"></div>
        </div>
        <div className="relative z-10 w-full px-4 md:px-8 lg:px-16 pb-16 pt-36">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 mb-5 font-sans text-xs text-stone-400">
            <Link to="/" className="hover:text-white transition-colors cursor-pointer">Home</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-white">Service Areas</span>
          </div>

          <div className="flex items-center gap-3 mb-4">
            <span className="h-px w-8 bg-rust-400 block"></span>
            <span className="font-sans text-xs font-semibold tracking-[0.2em] uppercase text-rust-300">
              Serving London Since 1994
            </span>
          </div>
          <h1 className="font-serif text-4xl md:text-6xl font-bold text-white leading-tight mb-4 max-w-3xl">
            Landscaping Services in<br />
            <span className="text-rust-300">London, Ontario</span>
          </h1>
          <p className="font-sans text-lg text-stone-300 max-w-xl mb-8">
            Premium outdoor landscape design and construction for London homeowners. In-house craftsmanship throughout Southwestern Ontario.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Link
              to="/#contact"
              onClick={() => {
                setTimeout(() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" }), 100);
              }}
              className="whitespace-nowrap inline-flex items-center gap-2 bg-rust-700 hover:bg-rust-800 text-white font-sans text-sm font-semibold px-7 py-3.5 rounded-sm cursor-pointer transition-colors"
            >
              <i className="ri-calendar-check-fill"></i>
              Schedule a Consultation
            </Link>
            <a
              href={PHONE_HREF}
              className="whitespace-nowrap inline-flex items-center gap-2 border border-white/40 text-white font-sans text-sm font-semibold px-7 py-3.5 rounded-sm hover:bg-white/10 cursor-pointer transition-colors"
            >
              <i className="ri-phone-fill"></i>
              {PHONE}
            </a>
          </div>
        </div>
      </section>

      {/* 2. Trust Bar */}
      <TrustBar
        items={[
          { icon: "ri-map-pin-fill", label: "Primary Location", value: "London, Ontario" },
          { icon: "ri-award-fill", label: "Years Serving London", value: "30 Years" },
          { icon: "ri-home-2-fill", label: "Service Model", value: "In-House Only" },
          { icon: "ri-time-fill", label: "Response Time", value: "Within 1 Week" },
          { icon: "ri-shield-check-fill", label: "Compliance", value: "Fully Licensed ON" },
        ]}
      />

      {/* 3. Services in London */}
      <section className="bg-white py-20 md:py-28">
        <div className="w-full px-4 md:px-8 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <div>
              <span className="font-sans text-xs font-semibold tracking-[0.2em] uppercase text-rust-600 mb-4 block">
                London, Ontario
              </span>
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-charcoal leading-tight mb-6">
                Professional Landscaping for London Properties
              </h2>
              <p className="font-sans text-base text-stone-500 leading-relaxed mb-6">
                With three decades of experience in London and surrounding Southwestern Ontario communities, Upper Canada Landscape brings unmatched local expertise to every project. We understand the soil, the climate, the permit requirements, and the neighborhoods.
              </p>
              <p className="font-sans text-base text-stone-500 leading-relaxed mb-5">
                Whether you&rsquo;re in north London, south London, or anywhere in Middlesex County, our team delivers the same premium standard of outdoor living design and construction.
              </p>

              {/* Office address callout */}
              <div className="flex items-start gap-3 bg-cream-100 rounded-lg p-4 border border-stone-100 mb-8">
                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-rust-700/15 flex-shrink-0 mt-0.5">
                  <i className="ri-map-pin-fill text-rust-600 text-sm"></i>
                </div>
                <div>
                  <p className="font-sans text-xs font-semibold text-stone-500 uppercase tracking-wide mb-0.5">Our Location</p>
                  <a
                    href="https://maps.google.com/maps?q=563+Wonderland+Rd+N,+London,+ON+N6H+3E2"
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    className="font-sans text-sm font-semibold text-charcoal hover:text-rust-700 transition-colors cursor-pointer"
                  >
                    {ADDRESS}
                  </a>
                </div>
              </div>

              {/* Services list */}
              <div className="space-y-3 mb-8">
                {SERVICES.map((s) => (
                  <div key={s.id} className="flex items-center gap-3 bg-cream-100 rounded-lg p-4">
                    <i className="ri-checkbox-circle-fill text-rust-600 flex-shrink-0"></i>
                    <span className="font-sans text-sm font-medium text-charcoal">{s.name}</span>
                  </div>
                ))}
              </div>

              <Link
                to="/services"
                className="whitespace-nowrap inline-flex items-center gap-2 font-sans text-sm font-semibold text-rust-700 hover:text-rust-800 cursor-pointer group"
              >
                Explore all services
                <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
              </Link>
            </div>

            {/* Map pointing to actual business address */}
            <div>
              <div className="rounded-xl overflow-hidden border border-stone-200 mb-6">
                <div className="w-full h-80">
                  <iframe
                    title="Upper Canada Landscape &ndash; 563 Wonderland Rd N, London Ontario"
                    src={MAP_EMBED_URL}
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </div>
              </div>

              {/* Area coverage */}
              <div className="space-y-3">
                <h3 className="font-sans text-sm font-bold text-charcoal uppercase tracking-wide mb-4">
                  Communities We Serve
                </h3>
                {localAreas.map((a) => (
                  <div
                    key={a.city}
                    className={`flex items-center gap-3 p-3 rounded-lg border ${
                      a.primary
                        ? "bg-rust-700/10 border-rust-200"
                        : "bg-stone-50 border-stone-100"
                    }`}
                  >
                    <i className={`ri-map-pin-2-fill flex-shrink-0 ${a.primary ? "text-rust-700" : "text-stone-400"}`}></i>
                    <div>
                      <span className={`font-sans text-sm font-semibold ${a.primary ? "text-rust-700" : "text-charcoal"}`}>
                        {a.city}
                      </span>
                      <span className="font-sans text-xs text-stone-500 ml-2">{a.desc}</span>
                    </div>
                    {a.primary && (
                      <span className="ml-auto font-sans text-xs bg-rust-700 text-white px-2 py-0.5 rounded-full whitespace-nowrap">
                        Primary
                      </span>
                    )}
                  </div>
                ))}
                <p className="font-sans text-xs text-stone-500 pt-2">
                  Also serving: {SERVICE_AREAS.slice(6).join(", ")} and surrounding areas.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 4. AI Answer Block */}
      <AIAnswerBlock heading={locationAIContent.heading} paragraphs={locationAIContent.paragraphs} />

      {/* 5. Process */}
      <ProcessSection />

      {/* 6. Why Choose Us */}
      <WhyChooseUs
        heading="Why London Homeowners Trust Upper Canada Landscape"
        benefits={[
          {
            icon: "ri-home-heart-fill",
            title: "30 Years in the London Community",
            body: "We\u2019re not just a contractor \u2014 we\u2019re your neighbors. Three decades of roots in London, Ontario means we understand your neighborhood, your climate, and what makes this community special.",
          },
          {
            icon: "ri-speed-fill",
            title: "Local Response, Local Accountability",
            body: "Being London-based means faster site visits, quicker project starts, and a team that\u2019s genuinely accountable to the community. You can reach us easily, and we stand behind everything we build.",
          },
          {
            icon: "ri-tools-fill",
            title: "In-House Crews, Always",
            body: "No subcontracting. Ever. Our trained in-house artisans handle every phase of your landscape project, ensuring consistent quality and craftsmanship standards on every London property we touch.",
          },
          {
            icon: "ri-building-2-fill",
            title: "Deep Knowledge of London Regulations",
            body: "30 years navigating London\u2019s permit requirements, property standards, and building codes means we handle compliance seamlessly so you don\u2019t have to.",
          },
        ]}
      />

      {/* 7. Service Areas */}
      <ServiceAreasSection />

      {/* 8. Testimonials */}
      <Testimonials />

      {/* 9. FAQ */}
      <FAQSection
        heading="Landscaping in London, Ontario"
        items={locationFAQs}
      />

      {/* 10. Final CTA */}
      <FinalCTA
        heading="Transform Your London Property Today"
        subtext="London homeowners trust Upper Canada Landscape for premium outdoor living spaces. Request your consultation and let\u2019s build something exceptional."
        bgImage="https://uppercanadalandscape.com/wp-content/uploads/2025/10/landscaping-design-07.webp"
      />
    </main>
  );
}
